import React, { useEffect, useState } from 'react';
import api from '../api';
import { RefreshCw, LayoutGrid, Search, Users, ShieldCheck, Trash2, Plus, Send, Lock } from 'lucide-react';
import toast from 'react-hot-toast';

const GroupsPage = ({ userPlan = 'free', onNavigateTab }) => {
    const [gruposNoWhats, setGruposNoWhats] = useState([]);
    const [gruposAtivosBD, setGruposAtivosBD] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtro, setFiltro] = useState('');
    const [processandoGrupo, setProcessandoGrupo] = useState(null);
    const [canalOficial, setCanalOficial] = useState('');
    const [modalConfirmacao, setModalConfirmacao] = useState(null); // { item, message, onConfirm }

    // Telegram states
    const [tgNome, setTgNome] = useState('');
    const [tgChatId, setTgChatId] = useState('');
    const [tgTipo, setTgTipo] = useState('canal'); // 'grupo' ou 'canal'
    const [tgCriando, setTgCriando] = useState(false);

    // WhatsApp Channel states
    const [waChannelLink, setWaChannelLink] = useState('');
    const [waChannelCriando, setWaChannelCriando] = useState(false);

    const carregarDados = async (showToast = false) => {
        setLoading(true);
        const toastId = showToast ? toast.loading("Carregando dados...") : null;
        try {
            const [resBD, resConfig] = await Promise.all([
                api.get('/grupos'),
                api.get('/config')
            ]);
            
            setCanalOficial(resConfig.data.whatsappCanal || '');
            setGruposAtivosBD(resBD.data);

            // WhatsApp Sync
            try {
                const resWhats = await api.get('/grupos/sync-whatsapp');
                const whatsPadronizado = resWhats.data.map(g => ({
                    ...g,
                    nome: g.name || g.nome || "Sem Nome",
                    whatsappId: typeof g.id === 'object' ? g.id._serialized : (g.whatsappId || g.id),
                    participantesCount: g.participants ? g.participants.length : 0
                }));
                setGruposNoWhats(whatsPadronizado);
            } catch (whatsErr) {
                console.error("Erro ao sincronizar com o WhatsApp:", whatsErr.message);
                if (showToast) {
                    toast.error("O WhatsApp está desconectado. Sincronização ignorada.", { id: toastId });
                    return;
                }
            }

            if (showToast) toast.success("Lista atualizada!", { id: toastId });
        } catch (err) {
            console.error(err);
            if (showToast) toast.error("Erro ao carregar dados", { id: toastId });
            else toast.error("Erro ao carregar grupos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { carregarDados(); }, []);

    // FUNÇÃO: Sincroniza apenas os membros DESTE grupo para a base global
    const sincronizarMembrosDoGrupo = async (grupoId) => {
        const isFree = userPlan === 'free' || userPlan === 'free_divulgador';
        if (isFree) {
            return toast.error("🔒 Sincronização de membros é exclusiva para planos pagos.");
        }
        setProcessandoGrupo(grupoId);
        try {
            const res = await api.post('/contatos/sincronizar-membros-grupo', { grupoId });
            toast.success(res.data.message);
        } catch (err) {
            toast.error("Erro ao extrair membros");
        } finally {
            setProcessandoGrupo(null);
        }
    };

    const toggleGrupoAtivo = async (item, confirmado = false) => {
        const idReal = item.whatsappId;
        const existente = gruposAtivosBD.find(db => String(db.whatsappId) === String(idReal) && db.plataforma === 'whatsapp');
        const novoStatus = existente ? !existente.ativo : true;
        
        try {
            if (existente) {
                const res = await api.put(`/grupos/${existente.id}`, { ativo: novoStatus, confirmado });
                if (res.data.requiresConfirmation) {
                    setModalConfirmacao({
                        item,
                        message: res.data.message,
                        onConfirm: (confirmedItem) => toggleGrupoAtivo(confirmedItem, true)
                    });
                    return;
                }
                setGruposAtivosBD(prev => prev.map(db => (String(db.whatsappId) === String(idReal) && db.plataforma === 'whatsapp') ? { ...db, ativo: novoStatus, lastActivatedAt: res.data.data?.lastActivatedAt || db.lastActivatedAt } : db));
                toast.success(novoStatus ? `Grupo ${item.nome} ativado!` : `Grupo ${item.nome} desativado!`);
            } else {
                const res = await api.post('/grupos', {
                    nome: item.nome,
                    whatsappId: idReal,
                    plataforma: 'whatsapp',
                    tipo: idReal.includes('@newsletter') ? 'canal' : 'grupo',
                    ativo: true,
                    confirmado
                });
                if (res.data.requiresConfirmation) {
                    setModalConfirmacao({
                        item,
                        message: res.data.message,
                        onConfirm: (confirmedItem) => toggleGrupoAtivo(confirmedItem, true)
                    });
                    return;
                }
                setGruposAtivosBD(prev => [...prev, res.data.data || res.data]);
                toast.success(`Grupo ${item.nome} ativado!`);
            }
            setModalConfirmacao(null);
        } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || "Erro ao atualizar status";
            toast.error(errMsg);
        }
    };

    const handleAddTelegram = async (e, confirmado = false, itemData = null) => {
        if (e && e.preventDefault) e.preventDefault();
        if (isFreePlan) {
            return toast.error("🔒 Canais e grupos do Telegram estão disponíveis a partir do plano Básico.");
        }
        const nomeFinal = itemData ? itemData.nome : tgNome.trim();
        const chatIdFinal = itemData ? itemData.whatsappId : tgChatId.trim();
        const tipoFinal = itemData ? itemData.tipo || 'canal' : tgTipo;

        if (!nomeFinal || !chatIdFinal) {
            return toast.error("Por favor, preencha o Nome e o Chat ID / Username do Telegram.");
        }
        setTgCriando(true);
        try {
            const res = await api.post('/grupos', {
                nome: nomeFinal,
                whatsappId: chatIdFinal,
                plataforma: 'telegram',
                tipo: tipoFinal,
                ativo: true,
                confirmado
            });
            if (res.data.requiresConfirmation) {
                setModalConfirmacao({
                    item: { nome: nomeFinal, whatsappId: chatIdFinal, plataforma: 'telegram', tipo: tipoFinal },
                    message: res.data.message,
                    onConfirm: (confirmedItem) => handleAddTelegram(null, true, confirmedItem)
                });
                setTgCriando(false);
                return;
            }
            setGruposAtivosBD(prev => [...prev, res.data.data || res.data]);
            toast.success("Destino do Telegram adicionado com sucesso!");
            if (!itemData) {
                setTgNome('');
                setTgChatId('');
                setTgTipo('canal');
            }
            setModalConfirmacao(null);
        } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || "Erro ao adicionar Telegram";
            toast.error(errMsg);
        } finally {
            setTgCriando(false);
        }
    };

    const toggleTelegramAtivo = async (item, confirmado = false) => {
        const novoStatus = !item.ativo;
        try {
            const res = await api.put(`/grupos/${item.id}`, { ativo: novoStatus, confirmado });
            if (res.data.requiresConfirmation) {
                setModalConfirmacao({
                    item,
                    message: res.data.message,
                    onConfirm: (confirmedItem) => toggleTelegramAtivo(confirmedItem, true)
                });
                return;
            }
            setGruposAtivosBD(prev => prev.map(db => String(db.id) === String(item.id) ? { ...db, ativo: novoStatus, lastActivatedAt: res.data.data?.lastActivatedAt || db.lastActivatedAt } : db));
            toast.success(novoStatus ? `Destino ${item.nome} ativado!` : `Destino ${item.nome} desativado!`);
            setModalConfirmacao(null);
        } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || "Erro ao atualizar status";
            toast.error(errMsg);
        }
    };

    const handleRemoveTelegram = async (id) => {
        try {
            await api.delete(`/grupos/${id}`);
            setGruposAtivosBD(prev => prev.filter(db => db.id !== id));
            toast.success("Destino removido!");
        } catch (err) {
            console.error(err);
            toast.error("Erro ao remover destino.");
        }
    };

    const handleAddWhatsappChannel = async (e) => {
        e.preventDefault();
        if (!waChannelLink.trim()) {
            return toast.error("Por favor, insira o link ou código do canal.");
        }
        setWaChannelCriando(true);
        const loadToast = toast.loading("Buscando metadados do canal no WhatsApp...");
        try {
            const res = await api.post('/grupos/add-canal-whatsapp', {
                link: waChannelLink.trim()
            });
            toast.success(res.data.message || "Canal adicionado com sucesso!", { id: loadToast });
            setWaChannelLink('');
            await carregarDados();
        } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || "Erro ao adicionar canal do WhatsApp.";
            toast.error(errMsg, { id: loadToast });
        } finally {
            setWaChannelCriando(false);
        }
    };

    const handleRemoveWhatsappChannel = async (id) => {
        if (!id) return;
        try {
            await api.delete(`/grupos/${id}`);
            setGruposAtivosBD(prev => prev.filter(db => db.id !== id));
            toast.success("Canal removido com sucesso!");
        } catch (err) {
            console.error(err);
            toast.error("Erro ao remover canal.");
        }
    };

    const setCanalOficialNoBanco = async (id) => {
        try {
            await api.post('/config', { whatsappCanal: id });
            setCanalOficial(id);
            toast.success("Canal oficial definido!");
        } catch (err) {
            toast.error("Erro ao definir canal");
        }
    };

    const renderCard = (g) => {
        const idReal = g.whatsappId;
        const dadosBD = gruposAtivosBD.find(db => String(db.whatsappId) === String(idReal) && db.plataforma === 'whatsapp');
        const estaAtivo = dadosBD?.ativo;
        const isSyncing = processandoGrupo === idReal;
        const isOfficial = String(canalOficial) === String(idReal);
        const isChannel = String(idReal).includes('@newsletter') || g.tipo === 'canal';

        const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
        const isSlotAtivo = dadosBD?.lastActivatedAt && 
            (Date.now() - new Date(dadosBD.lastActivatedAt).getTime() < COOLDOWN_MS);
        const dataExpiracao = isSlotAtivo 
            ? new Date(new Date(dadosBD.lastActivatedAt).getTime() + COOLDOWN_MS).toLocaleDateString('pt-BR') 
            : null;

        return (
            <div key={idReal} style={estaAtivo ? cardAtivo : cardInativo}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ ...groupName(estaAtivo), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.nome}</div>
                        <div style={{ ...groupId(estaAtivo), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{idReal}</div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', marginTop: '4px', textTransform: 'capitalize', color: '#64748b' }}>
                            {isChannel ? '📢 Canal' : '👥 Grupo'}
                        </div>
                        {isSlotAtivo && (
                            <div style={{
                                fontSize: '11px', 
                                marginTop: '6px', 
                                color: estaAtivo ? '#25D366' : '#ee4d2d', 
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                🔒 Vinculado ao Plano (Até {dataExpiracao})
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isOfficial && <ShieldCheck size={20} color={estaAtivo ? "#fff" : "#ee4d2d"} />}
                        <div 
                            onClick={() => toggleGrupoAtivo(g)}
                            style={{
                                width: '48px',
                                height: '24px',
                                borderRadius: '12px',
                                backgroundColor: estaAtivo ? '#25D366' : '#cbd5e1',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: '0.3s',
                                border: 'none'
                            }}
                        >
                            <div style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                position: 'absolute',
                                top: '3px',
                                left: estaAtivo ? '27px' : '3px',
                                transition: '0.3s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                    </div>
                </div>

                <div style={actionsContainer}>
                    {isChannel ? (
                        <>
                            <button
                                onClick={() => setCanalOficialNoBanco(idReal)}
                                style={btnAction(estaAtivo)}
                                disabled={isOfficial}
                            >
                                <ShieldCheck size={14} />
                                {isOfficial ? 'Canal Oficial' : 'Definir como Oficial'}
                            </button>

                            {dadosBD && (
                                <button
                                    onClick={() => handleRemoveWhatsappChannel(dadosBD.id)}
                                    style={{
                                        ...btnAction(estaAtivo),
                                        color: '#ef4444',
                                        backgroundColor: '#fef2f2',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Trash2 size={14} />
                                    Remover
                                </button>
                            )}
                        </>
                    ) : (
                        <button
                            onClick={() => sincronizarMembrosDoGrupo(idReal)}
                            style={btnAction(estaAtivo)}
                            disabled={isSyncing}
                        >
                            {isSyncing ? <RefreshCw size={14} className="spin" /> : <Users size={14} />}
                            {isSyncing ? 'Lendo...' : 'Sincronizar Membros'}
                        </button>
                    )}

                    <div style={badgeMembros(estaAtivo)}>
                        {g.participantesCount || 0} {isChannel ? 'seguidores' : 'membros'}
                    </div>
                </div>
            </div>
        );
    };

    const renderTelegramCard = (tg) => {
        const estaAtivo = tg.ativo;
        const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
        const isSlotAtivo = tg.lastActivatedAt && 
            (Date.now() - new Date(tg.lastActivatedAt).getTime() < COOLDOWN_MS);
        const dataExpiracao = isSlotAtivo 
            ? new Date(new Date(tg.lastActivatedAt).getTime() + COOLDOWN_MS).toLocaleDateString('pt-BR') 
            : null;

        return (
            <div key={tg.id} style={estaAtivo ? cardTgAtivo : cardTgInativo}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ ...groupName(estaAtivo), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tg.nome}</div>
                        <div style={{ ...groupId(estaAtivo), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tg.whatsappId}</div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', marginTop: '4px', textTransform: 'capitalize', color: '#64748b' }}>
                            {tg.tipo === 'canal' ? '📢 Canal' : '👥 Grupo'}
                        </div>
                        {isSlotAtivo && (
                            <div style={{
                                fontSize: '11px', 
                                marginTop: '6px', 
                                color: estaAtivo ? '#0088cc' : '#ee4d2d', 
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                🔒 Vinculado ao Plano (Até {dataExpiracao})
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div 
                            onClick={() => toggleTelegramAtivo(tg)}
                            style={{
                                width: '48px',
                                height: '24px',
                                borderRadius: '12px',
                                backgroundColor: estaAtivo ? '#0088cc' : '#cbd5e1',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: '0.3s',
                                border: 'none'
                            }}
                        >
                            <div style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                position: 'absolute',
                                top: '3px',
                                left: estaAtivo ? '27px' : '3px',
                                transition: '0.3s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                    </div>
                </div>

                <div style={actionsContainer}>
                    <button
                        onClick={() => handleRemoveTelegram(tg.id)}
                        style={{
                            ...btnAction(estaAtivo),
                            color: '#ef4444',
                            backgroundColor: '#fef2f2',
                            cursor: 'pointer'
                        }}
                    >
                        <Trash2 size={14} />
                        Remover
                    </button>

                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0088cc' }}>
                        Telegram
                    </div>
                </div>
            </div>
        );
    };

    const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
    const slotsUsados = gruposAtivosBD.filter(db => db.lastActivatedAt && (Date.now() - new Date(db.lastActivatedAt).getTime() < COOLDOWN_MS)).length;
    const cleanPlan = (userPlan || 'free').toLowerCase();
    const isFreePlan = cleanPlan === 'free' || cleanPlan.startsWith('free');
    const isBasicPlan = cleanPlan === 'basic' || cleanPlan.startsWith('basic');
    const limitePlan = isFreePlan ? 1 : (isBasicPlan ? 5 : Infinity);

    // Combina os grupos sincronizados com os salvos no BD para garantir que todos fiquem visíveis e possam ser desativados/gerenciados
    const whatsappExibicao = [...gruposNoWhats];
    gruposAtivosBD.forEach(dbG => {
        if (dbG.plataforma === 'whatsapp') {
            const jaExiste = whatsappExibicao.some(g => String(g.whatsappId) === String(dbG.whatsappId));
            if (!jaExiste) {
                whatsappExibicao.push({
                    nome: dbG.nome || "Grupo sem nome",
                    whatsappId: dbG.whatsappId,
                    plataforma: 'whatsapp',
                    tipo: dbG.tipo || (dbG.whatsappId.includes('@newsletter') ? 'canal' : 'grupo'),
                    ativo: dbG.ativo,
                    participantesCount: 0
                });
            }
        }
    });

    return (
        <div className="page-wrapper" style={{ fontFamily: 'sans-serif' }}>
            <div className="mobile-flex-col" style={{ ...headerStyle, gap: '15px' }}>
                <div>
                    <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <LayoutGrid size={32} color="#ee4d2d" /> Grupos e Canais
                    </h1>
                    <p style={{ color: '#64748b' }}>Gerencie seus destinos de postagem e canais oficiais.</p>
                </div>
            </div>
 
            {/* Banner de Slots para Planos Free/Básico */}
            {(isFreePlan || isBasicPlan) && (
                <div style={{
                    backgroundColor: '#fff',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    borderLeft: `5px solid ${isFreePlan ? '#ee4d2d' : '#0088cc'}`,
                    marginBottom: '25px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                }}>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        📊 Uso de Slots de Canais/Grupos: {slotsUsados} de {limitePlan}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
                        Seu plano permite que você ative e utilize até <strong>{limitePlan}</strong> destino(s) (grupo ou canal) por mês. Cada destino ativado é fixado ao seu plano por 30 dias. Você poderá ativá-lo ou desativá-lo à vontade, mas não poderá trocá-lo por outro grupo ou canal até que o prazo de 30 dias expire.
                    </div>
                </div>
            )}
 
            <div style={searchBar}>
                <Search size={20} color="#94a3b8" />
                <input
                    style={searchInput}
                    placeholder="Buscar grupo..."
                    value={filtro}
                    onChange={e => setFiltro(e.target.value)}
                />
            </div>
 
            {/* --- SEÇÃO DO WHATSAPP --- */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px', color: '#1e293b', margin: 0 }}>
                    <Users size={20} color="#25D366" /> WhatsApp (Canais e Grupos)
                </h2>
                <button onClick={() => carregarDados(true)} style={{ ...btnRefresh, backgroundColor: '#25D366', padding: '8px 14px', fontSize: '13px' }} disabled={loading}>
                    <RefreshCw size={16} className={loading ? 'spin' : ''} />
                    Sincronizar Lista
                </button>
            </div>
            
            {/* Formulário Adicionar Canal WhatsApp */}
            <form onSubmit={handleAddWhatsappChannel} style={formCard}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
                    <div style={{ ...inputGroupStyle, flex: 2 }}>
                        <label style={labelStyle}>Link ou Código do Canal do WhatsApp</label>
                        <input
                            style={inputStyle}
                            placeholder="Ex: https://whatsapp.com/channel/0029Vb8T4LhBlHpZP3pZcE1t ou 0029Vb8T4LhBlHpZP3pZcE1t"
                            value={waChannelLink}
                            onChange={e => setWaChannelLink(e.target.value)}
                            required
                        />
                    </div>
                    <button type="submit" style={{ ...btnAdd, backgroundColor: '#25D366' }} disabled={waChannelCriando}>
                        <Plus size={16} />
                        {waChannelCriando ? 'Adicionando...' : 'Adicionar Canal'}
                    </button>
                </div>
            </form>
 
            <div className="responsive-grid" style={{ marginBottom: whatsappExibicao.length === 0 ? '10px' : '30px' }}>
                {whatsappExibicao
                    .filter(g => g.nome.toLowerCase().includes(filtro.toLowerCase()))
                    .map(g => renderCard(g))
                }
            </div>

            {whatsappExibicao.length === 0 && (
                <div style={{
                    padding: '30px',
                    textAlign: 'center',
                    background: '#f8fafc',
                    borderRadius: '14px',
                    border: '1px dashed #cbd5e1',
                    marginBottom: '35px'
                }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📱</div>
                    <h4 style={{ color: '#1e293b', fontWeight: 'bold', margin: '0 0 6px 0', fontSize: '15px' }}>
                        Nenhum grupo de WhatsApp sincronizado ainda
                    </h4>
                    <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.5' }}>
                        Para carregar e ativar seu grupo do WhatsApp, conecte seu WhatsApp na aba <strong>Configurações</strong> e clique no botão <strong>Sincronizar Lista</strong> acima.
                    </p>
                    {onNavigateTab && (
                        <button
                            type="button"
                            onClick={() => onNavigateTab('config')}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#25D366',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                fontWeight: 'bold',
                                fontSize: '13px',
                                cursor: 'pointer',
                                boxShadow: '0 4px 10px rgba(37, 211, 102, 0.25)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            ⚙️ Conectar WhatsApp nas Configurações
                        </button>
                    )}
                </div>
            )}

            {/* --- SEÇÃO DO TELEGRAM --- */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px', marginBottom: '15px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px', color: '#1e293b', margin: 0 }}>
                    <Send size={20} color="#0088cc" /> Telegram (Canais e Grupos)
                </h2>
                {isFreePlan && (
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#0088cc', backgroundColor: '#e0f2fe', padding: '4px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        🔒 Disponível nos Planos Básico e Premium
                    </span>
                )}
            </div>

            {isFreePlan ? (
                <div style={{
                    borderRadius: '16px',
                    border: '1px solid #bfdbfe',
                    backgroundColor: '#f8fafc',
                    padding: '24px',
                    marginBottom: '30px'
                }}>
                    {/* Banner explicativo */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '15px',
                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        padding: '18px 20px',
                        borderRadius: '12px',
                        marginBottom: '20px',
                        border: '1px solid #bfdbfe'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '30px' }}>🔒</span>
                            <div>
                                <h4 style={{ margin: 0, color: '#1e40af', fontSize: '15px', fontWeight: 'bold' }}>
                                    Recurso Bloqueado no Plano Free (Disponível em Básico e Premium)
                                </h4>
                                <p style={{ margin: '4px 0 0 0', color: '#1e3a8a', fontSize: '13px', lineHeight: '1.4' }}>
                                    A integração com canais e grupos do Telegram para envio de ofertas com entrega instantânea está disponível a partir do <strong>Plano Básico</strong>.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => onNavigateTab ? onNavigateTab('billing') : (window.location.hash = '#billing')}
                            style={{
                                padding: '10px 22px',
                                backgroundColor: '#0088cc',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                fontWeight: 'bold',
                                fontSize: '13px',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(0, 136, 204, 0.3)',
                                transition: '0.2s',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Fazer Upgrade Agora ✨
                        </button>
                    </div>

                    {/* Formulário com aparência desabilitada / preview */}
                    <div style={{ opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
                        <div style={formCard}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Nome do Destino</label>
                                    <input
                                        style={inputStyle}
                                        placeholder="Ex: Achados Ofertas"
                                        disabled
                                        value=""
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Chat ID ou Username (@)</label>
                                    <input
                                        style={inputStyle}
                                        placeholder="Ex: -100123456789 ou @meu_canal"
                                        disabled
                                        value=""
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Tipo</label>
                                    <select style={selectStyle} disabled>
                                        <option value="canal">Canal (📢)</option>
                                    </select>
                                </div>
                                <button type="button" style={{ ...btnAdd, backgroundColor: '#94a3b8', cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '6px' }} disabled>
                                    <Lock size={15} /> Bloqueado no Free
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Formulário Telegram */}
                    <form onSubmit={handleAddTelegram} style={formCard}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Nome do Destino</label>
                                <input
                                    style={inputStyle}
                                    placeholder="Ex: Achados Ofertas"
                                    value={tgNome}
                                    onChange={e => setTgNome(e.target.value)}
                                    required
                                />
                            </div>
                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Chat ID ou Username (@)</label>
                                <input
                                    style={inputStyle}
                                    placeholder="Ex: -100123456789 ou @meu_canal"
                                    value={tgChatId}
                                    onChange={e => setTgChatId(e.target.value)}
                                    required
                                />
                            </div>
                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Tipo</label>
                                <select
                                    style={selectStyle}
                                    value={tgTipo}
                                    onChange={e => setTgTipo(e.target.value)}
                                >
                                    <option value="canal">Canal (📢)</option>
                                    <option value="grupo">Grupo (👥)</option>
                                </select>
                            </div>
                            <button type="submit" style={btnAdd} disabled={tgCriando}>
                                <Plus size={16} />
                                {tgCriando ? 'Salvando...' : 'Adicionar'}
                            </button>
                        </div>
                    </form>

                    {/* Grid Telegram */}
                    <div className="responsive-grid">
                        {gruposAtivosBD
                            .filter(g => g.plataforma === 'telegram')
                            .filter(g => g.nome.toLowerCase().includes(filtro.toLowerCase()))
                            .map(tg => renderTelegramCard(tg))
                        }
                    </div>
                </>
            )}

            {/* Modal de Confirmação de Slot */}
            {modalConfirmacao && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '480px',
                        width: '90%',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px'
                    }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ee4d2d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            ⚠️ Confirmar Ativação de Destino
                        </div>
                        <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                            {modalConfirmacao.message}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                            <button
                                onClick={() => setModalConfirmacao(null)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    backgroundColor: '#fff',
                                    color: '#475569',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    modalConfirmacao.onConfirm(modalConfirmacao.item);
                                    setModalConfirmacao(null);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: '#ee4d2d',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Confirmar Vinculação
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`.spin { animation: spin 2s infinite linear; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

// --- Estilos Auxiliares ---
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' };
const actionsContainer = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '15px', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '10px' };
const groupName = (ativo) => ({ fontWeight: 'bold', fontSize: '15px', color: '#1e293b' });
const groupId = (ativo) => ({ fontSize: '10px', opacity: 0.7, color: '#64748b' });
const btnAction = (ativo) => ({
    display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', borderRadius: '6px',
    border: 'none', backgroundColor: '#f1f5f9',
    color: '#475569', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold'
});
const badgeMembros = (ativo) => ({
    fontSize: '11px', fontWeight: 'bold', color: '#ee4d2d'
});
const btnRefresh = { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#ee4d2d', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' };
const searchBar = { display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#fff', padding: '12px 15px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #e2e8f0' };
const searchInput = { border: 'none', outline: 'none', width: '100%', fontSize: '15px' };
const baseCard = { padding: '20px', borderRadius: '15px', transition: '0.3s all ease', border: '1px solid #e2e8f0' };
const cardInativo = { ...baseCard, backgroundColor: '#fff' };
const cardAtivo = { ...baseCard, backgroundColor: '#fff', borderColor: '#25D366', boxShadow: '0 4px 12px rgba(37, 211, 102, 0.15)' };
const cardTgInativo = { ...baseCard, backgroundColor: '#fff' };
const cardTgAtivo = { ...baseCard, backgroundColor: '#fff', borderColor: '#0088cc', boxShadow: '0 4px 12px rgba(0, 136, 204, 0.15)' };
const formCard = { backgroundColor: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '30px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#475569' };
const inputStyle = { padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc', transition: '0.2s border-color' };
const selectStyle = { ...inputStyle, cursor: 'pointer' };
const btnAdd = { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', backgroundColor: '#0088cc', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', height: '42px', alignSelf: 'flex-end' };

export default GroupsPage;